import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Users, Check } from 'lucide-react';
import { reservationService, type ApiReservation, type ApiBookingInfo } from '@kss/api';
import { useAuth } from '@kss/auth';
import { Card, Button, useToast, cn, FieldError, useField, v, applyGuard } from '@kss/ui';

/** A booking can't be requested further out than this — a date picker will happily accept 9999. */
const MAX_BOOKING_DAYS = 90;
/** Above this, the outlet takes the booking by phone. Guards the "7+" counter, which had no ceiling. */
const MAX_PARTY = 50;
const NOTE_MAX = 200;

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-warning/15 text-warning',
  confirmed: 'bg-veg-light text-veg',
  seated: 'bg-primary-light text-primary',
  cancelled: 'bg-danger/15 text-danger',
  noshow: 'bg-danger/15 text-danger'
};

/** Today as yyyy-MM-dd — what <input type="date"> needs (its own value format).
 *  Built from local parts, NOT toISOString(), which shifts the day in +TZ zones. */
function todayIso() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The furthest-out date the picker will accept, as yyyy-MM-dd. Without a `max`, the native
 *  picker lets a keyboard-entered year like 9999 through to the API. */
function maxBookingIso() {
  const d = new Date();
  d.setDate(d.getDate() + MAX_BOOKING_DAYS);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** yyyy-MM-dd → dd-MM-yyyy — the format we show the customer and send to the API. */
function toDisplayDate(iso: string) {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}-${m}-${y}` : iso;
}

/** "7:30 PM" → "19:30" (24h), which is what the API expects for `time`. */
function to24h(slot: string) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(slot.trim());
  if (!m) return slot;
  let h = Number(m[1]);
  const min = m[2];
  const pm = m[3]!.toUpperCase() === 'PM';
  if (pm && h !== 12) h += 12;
  if (!pm && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
}

/** Customer booking workflow — request a table and track its status. */
export default function Booking() {
  const { user } = useAuth();
  const toast = useToast();
  const phone = user?.phone ?? '';

  const [party, setParty] = useState(2);
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState('');
  const [table, setTable] = useState(''); // '' = let the outlet assign
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState<ApiReservation[]>([]);
  // Slots + bookable tables come from the outlet's real open/close hours and the tables
  // the panel has enabled — never hardcoded here.
  const [info, setInfo] = useState<ApiBookingInfo | null>(null);

  useEffect(() => {
    reservationService
      .bookingInfo()
      .then((i) => {
        setInfo(i);
        setTime((t) => (t && i.slots.includes(t) ? t : (i.slots[Math.floor(i.slots.length / 2)] ?? '')));
      })
      .catch(() => {
        /* offline → the form stays disabled rather than offering fake slots */
      });
  }, []);

  const load = useMemo(
    () => () => {
      if (!phone) return;
      reservationService
        .mine(phone)
        .then(setBookings)
        .catch(() => {
          /* keep the current list on failure */
        });
    },
    [phone]
  );

  useEffect(() => {
    load();
  }, [load]);

  // The date arrives as yyyy-MM-dd from the native picker; the rules read either shape.
  const dateField = useField(date, [
    v.required('Date'),
    v.date('Date', { notPast: true, maxYearsAhead: 1, max: new Date(maxBookingIso()) })
  ]);
  const noteField = useField(note, [v.maxLength(NOTE_MAX, 'Note'), ...v.safeText('Note')]);
  const timeField = useField(time, [v.required('Time')]);
  const canSubmit = Boolean(phone) && dateField.isValid && noteField.isValid && timeField.isValid;

  async function submit() {
    if (!phone) {
      toast.show('Sign in to request a booking', 'error');
      return;
    }
    dateField.markTouched();
    timeField.markTouched();
    noteField.markTouched();
    if (!canSubmit) return;
    setBusy(true);
    const display = toDisplayDate(date);
    try {
      await reservationService.create({
        name: user?.name ?? 'Guest',
        phone,
        party,
        when: `${display} · ${time}`,
        date: display, // dd-MM-yyyy
        time: to24h(time), // HH:mm inside the outlet's open/close hours
        tables: table || undefined, // must be a table the panel has enabled
        note: note.trim() || undefined
      });
      toast.show('Booking requested — we’ll confirm shortly', 'success');
      setNote('');
      load();
    } catch (e) {
      // Surface the server's reason (past date, disabled table) rather than a generic error.
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not submit the booking, try again';
      toast.show(msg, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className='mx-auto max-w-[640px] px-4 py-6 md:px-6'>
      <h1 className='flex items-center gap-2 font-display text-xl font-extrabold'>
        <CalendarClock size={20} className='text-primary' /> Book a table
      </h1>
      <p className='mt-1 text-[13px] text-content-muted'>Request a slot — you’ll see the status update below.</p>

      {/* Request form */}
      <Card className='mt-4 flex flex-col gap-4 p-4'>
        <div>
          <Label>Guests</Label>
          <div className='flex items-center gap-2'>
            <Users size={16} className='text-content-muted' />
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <button
                key={n}
                onClick={() => setParty(n)}
                className={cn(
                  'h-9 w-9 rounded-lg border text-[13px] font-bold transition',
                  party === n
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-content-secondary hover:border-primary'
                )}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setParty((p) => Math.min(p + 1, MAX_PARTY))}
              disabled={party >= MAX_PARTY}
              className='h-9 rounded-lg border border-border px-2.5 text-[13px] font-bold text-content-secondary hover:border-primary disabled:opacity-50'
            >
              7+
            </button>
          </div>
        </div>

        <div>
          <Label>Date</Label>
          <input
            type='date'
            value={date}
            // Today or later only — the server enforces this too. `max` bounds the other end:
            // without it a typed year (9999) reaches the API and sorts to the end of time.
            min={todayIso()}
            max={maxBookingIso()}
            onChange={(e) => setDate(e.target.value || todayIso())}
            onBlur={dateField.onBlur}
            aria-invalid={dateField.error ? true : undefined}
            className={cn(
              'w-full rounded-lg border-[1.5px] bg-surface-card px-3 py-2.5 text-sm focus:outline-none',
              dateField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            )}
          />
          <FieldError error={dateField.error} />
          {/* The picker renders in the browser's own locale, so show the booking date in
              dd-MM-yyyy — the format we send and confirm back. */}
          <div className='mt-1 text-[11.5px] text-content-muted'>
            Booking for <b className='text-content-secondary'>{toDisplayDate(date)}</b> (dd-MM-yyyy)
          </div>
        </div>

        <div>
          <Label>Time</Label>
          {info && (
            <div className='mb-1.5 text-[11.5px] text-content-muted'>
              Open {info.openTime} – {info.closeTime}
            </div>
          )}
          <div className='flex flex-wrap gap-2'>
            {(info?.slots ?? []).map((tm) => (
              <button
                key={tm}
                onClick={() => setTime(tm)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-[12.5px] font-semibold transition',
                  time === tm
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-content-secondary hover:border-primary'
                )}
              >
                {tm}
              </button>
            ))}
            {!info && <span className='text-[12.5px] text-content-muted'>Loading times…</span>}
          </div>
        </div>

        <div>
          <Label>Table</Label>
          {/* Only tables that exist AND are enabled in the panel — the count matches it exactly. */}
          {info && info.tables.length === 0 ? (
            <div className='rounded-lg border border-dashed border-border px-3 py-3 text-[12.5px] text-content-muted'>
              No tables are open for booking right now — request a slot and we’ll assign one.
            </div>
          ) : (
            <>
              <div className='mb-1.5 text-[11.5px] text-content-muted'>
                {info ? `${info.tables.length} table${info.tables.length === 1 ? '' : 's'} available` : 'Loading tables…'}
              </div>
              <div className='flex flex-wrap gap-2'>
                <button
                  onClick={() => setTable('')}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-[12.5px] font-semibold transition',
                    table === ''
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-content-secondary hover:border-primary'
                  )}
                >
                  Any table
                </button>
                {(info?.tables ?? []).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTable(t.id)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-[12.5px] font-semibold transition',
                      table === t.id
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-content-secondary hover:border-primary'
                    )}
                  >
                    {t.label} · {t.seats}p{t.floor ? ` · ${t.floor}` : ''}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          <Label>Note (optional)</Label>
          <input
            value={note}
            // Guarded on change, so a pasted note is shaped and capped exactly like a typed one.
            onChange={(e) => setNote(applyGuard(e.target.value, 'text', NOTE_MAX))}
            onBlur={noteField.onBlur}
            maxLength={NOTE_MAX}
            placeholder='Window seat, birthday…'
            aria-invalid={noteField.error ? true : undefined}
            className={cn(
              'w-full rounded-lg border-[1.5px] bg-surface-card px-3 py-2.5 text-sm focus:outline-none',
              noteField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
            )}
          />
          <FieldError error={noteField.error} />
        </div>

        <FieldError error={timeField.error} />
        <Button block size='lg' onClick={submit} disabled={busy || !canSubmit}>
          {busy ? 'Requesting…' : 'Request booking'}
        </Button>
      </Card>

      {/* My bookings */}
      <h2 className='mb-2 mt-6 font-display text-[15px] font-bold'>My bookings</h2>
      {bookings.length === 0 ? (
        <div className='rounded-xl border border-dashed border-border py-10 text-center text-[13px] text-content-muted'>
          No booking requests yet.
        </div>
      ) : (
        <div className='flex flex-col gap-2.5'>
          {bookings.map((b) => (
            <Card key={b.id} className='flex items-center gap-3 p-3.5'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary'>
                <CalendarClock size={18} />
              </div>
              <div className='min-w-0 flex-1'>
                <div className='font-display text-[13.5px] font-bold'>{b.when}</div>
                <div className='text-[11.5px] text-content-muted'>
                  {b.party} guest{b.party > 1 ? 's' : ''}
                  {/* The table the outlet reserved (blank until they assign one). */}
                  {b.tables.length > 0 ? ` · ${b.tables.join(', ')}` : ''}
                  {b.note ? ` · ${b.note}` : ''} · {b.id}
                </div>
              </div>
              <span
                className={cn(
                  'flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-bold capitalize',
                  STATUS_STYLE[b.status] ?? 'bg-surface-card-hover text-content-secondary'
                )}
              >
                {b.status === 'confirmed' && <Check size={11} />}
                {b.status}
              </span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-muted'>{children}</div>;
}
